/**
 * Admin Accounts management page.
 *
 * Lists all accounts with per-row actions (Edit, Switch, Lock, Suspend) and an
 * expandable users sub-table for provisioning users under an account. Access is
 * gated to admins in addition to the backend authorization.
 *
 * Lock / Suspend are intentionally dummy no-ops for now (see feature spec) —
 * the backend endpoints exist but the UI does not wire them yet.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Table, Button, Badge, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding,
    faPen,
    faRightToBracket,
    faLock,
    faBan,
    faPlus,
    faChevronRight,
    faChevronDown,
    faExclamationTriangle,
    faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../store';
import {
    fetchAccounts,
    fetchAccountUsers,
    switchAccount,
    clearAccountsError,
} from '../store/accountsSlice';
import { Account, AccountStatus } from '../types/account';
import AccountFormModal from '../components/accounts/AccountFormModal';
import AddUserModal from '../components/accounts/AddUserModal';
import UsersTable from '../components/users/UsersTable';

const STATUS_VARIANT: Record<AccountStatus, string> = {
    active: 'success',
    locked: 'warning',
    suspended: 'danger',
};

function formatDate(iso: string): string {
    const date = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

const AccountsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const {
        accounts,
        isLoading,
        error,
        isSwitching,
        usersByAccount,
        loadingUsersFor,
        impersonatedAccountId,
    } = useSelector((state: RootState) => state.accounts);
    const userRole = useSelector((state: RootState) => state.auth.user?.role);
    const currentAccountId = useSelector((state: RootState) => state.auth.user?.account_id);
    const isAdmin = userRole === 'admin';

    // Local UI state.
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [editingAccount, setEditingAccount] = useState<Account | null>(null);
    const [addUserFor, setAddUserFor] = useState<Account | null>(null);

    useEffect(() => {
        if (isAdmin) {
            dispatch(fetchAccounts());
        }
    }, [isAdmin, dispatch]);

    const handleToggleExpand = useCallback(
        (accountId: string) => {
            setExpandedId((prev) => {
                const next = prev === accountId ? null : accountId;
                if (next && !usersByAccount[accountId]) {
                    dispatch(fetchAccountUsers(accountId));
                }
                return next;
            });
        },
        [dispatch, usersByAccount]
    );

    const handleAddClick = useCallback(() => {
        setEditingAccount(null);
        setShowForm(true);
    }, []);

    const handleEditClick = useCallback((account: Account) => {
        setEditingAccount(account);
        setShowForm(true);
    }, []);

    const handleSwitch = useCallback(
        (accountId: string) => {
            dispatch(switchAccount(accountId));
        },
        [dispatch]
    );

    // Role gate — mirror the nav gating so a direct navigation is also blocked.
    if (!isAdmin) {
        return (
            <Container fluid className="h-100 d-flex align-items-center justify-content-center">
                <Row>
                    <Col className="text-center text-muted">
                        <FontAwesomeIcon icon={faUserShield} size="4x" className="mb-3" />
                        <h3>Accounts</h3>
                        <p>Only an administrator can manage accounts.</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3>
                        <FontAwesomeIcon icon={faBuilding} className="me-2" />
                        Accounts
                    </h3>
                    <p className="text-muted mb-0">
                        Manage customer accounts, provision users, and switch into an
                        account to operate on its data.
                    </p>
                </div>
                <Button variant="primary" onClick={handleAddClick}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Account
                </Button>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => dispatch(clearAccountsError())}
                >
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    {error}
                </Alert>
            )}

            {isLoading && accounts.length === 0 ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading accounts...</span>
                    </Spinner>
                </div>
            ) : accounts.length === 0 ? (
                <div className="text-center text-muted py-5">
                    <FontAwesomeIcon icon={faBuilding} size="4x" className="mb-3" />
                    <h4>No accounts yet</h4>
                    <p>Create an account to get started.</p>
                </div>
            ) : (
                <Table hover responsive>
                    <thead>
                        <tr>
                            <th style={{ width: '2rem' }} />
                            <th>Name</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.map((account) => {
                            const isExpanded = expandedId === account.id;
                            const isCurrent = account.id === currentAccountId;
                            const users = usersByAccount[account.id];
                            const usersLoading = loadingUsersFor.includes(account.id);
                            return (
                                <React.Fragment key={account.id}>
                                    <tr>
                                        <td className="align-middle">
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 text-decoration-none"
                                                onClick={() => handleToggleExpand(account.id)}
                                                aria-label={
                                                    isExpanded ? 'Collapse users' : 'Expand users'
                                                }
                                                aria-expanded={isExpanded}
                                            >
                                                <FontAwesomeIcon
                                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                                />
                                            </Button>
                                        </td>
                                        <td className="align-middle">
                                            {account.account_name}
                                            {isCurrent && (
                                                <Badge bg="info" className="ms-2">
                                                    {impersonatedAccountId === account.id
                                                        ? 'Impersonating'
                                                        : 'Current'}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="align-middle">
                                            <Badge bg={STATUS_VARIANT[account.status]}>
                                                {account.status}
                                            </Badge>
                                        </td>
                                        <td className="align-middle">
                                            {formatDate(account.created_at)}
                                        </td>
                                        <td className="text-end align-middle">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleEditClick(account)}
                                                title="Edit account"
                                            >
                                                <FontAwesomeIcon icon={faPen} className="me-1" />
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-2"
                                                onClick={() => handleSwitch(account.id)}
                                                disabled={isSwitching || isCurrent}
                                                title={
                                                    isCurrent
                                                        ? 'This is your current account'
                                                        : `Switch to ${account.account_name}`
                                                }
                                            >
                                                <FontAwesomeIcon
                                                    icon={faRightToBracket}
                                                    className="me-1"
                                                />
                                                Switch
                                            </Button>
                                            {/* Lock / Suspend are dummy no-ops for now (see spec). */}
                                            <Button
                                                variant="outline-warning"
                                                size="sm"
                                                className="me-2"
                                                title="Lock (coming soon)"
                                                onClick={() => undefined}
                                            >
                                                <FontAwesomeIcon icon={faLock} className="me-1" />
                                                Lock
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                title="Suspend (coming soon)"
                                                onClick={() => undefined}
                                            >
                                                <FontAwesomeIcon icon={faBan} className="me-1" />
                                                Suspend
                                            </Button>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr>
                                            <td colSpan={5} className="bg-body-tertiary">
                                                <UsersTable
                                                    users={users ?? []}
                                                    loading={usersLoading}
                                                    title="Users"
                                                    onAddUser={() => setAddUserFor(account)}
                                                    emptyText="No users in this account yet."
                                                />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            <AccountFormModal
                show={showForm}
                onHide={() => setShowForm(false)}
                account={editingAccount}
            />

            {addUserFor && (
                <AddUserModal
                    show={!!addUserFor}
                    onHide={() => setAddUserFor(null)}
                    accountId={addUserFor.id}
                    accountName={addUserFor.account_name}
                />
            )}
        </Container>
    );
};

export default AccountsPage;
